import React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import SendIcon from '@mui/icons-material/Send';
import MailIcon from '@mui/icons-material/Mail';
import { PrimaryButton } from './workspaceModalParts';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN } from './qcTheme';

// Shown once per session when the assessor marks their last open QC issue Ready — their side of the loop is clear.

const Body = styled('div')({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '34px 36px 26px',
});

const IconBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '46px',
  height: '46px',
  borderRadius: '50%',
  background: 'rgba(25,118,210,0.1)',
  color: ACCENT_BLUE,
});

const Eyebrow = styled('div')({
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.9px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.42)',
  marginTop: '18px',
});

const Title = styled('div')({
  fontSize: '1.4rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  letterSpacing: '-0.2px',
  marginTop: '5px',
});

const Lede = styled('div')({
  fontSize: '0.9rem',
  lineHeight: 1.55,
  color: 'rgba(0,0,0,0.6)',
  marginTop: '7px',
});

const StatsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1px 1fr',
  alignItems: 'center',
  marginTop: '26px',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  overflow: 'hidden',
});

const StatCell = styled('div')({
  padding: '16px 18px',
});

const StatValue = styled('div', {
  shouldForwardProp: prop => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  fontSize: '1.6rem',
  fontWeight: 400,
  color,
  lineHeight: 1,
}));

const StatLabel = styled('div')({
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.5)',
  marginTop: '7px',
});

const StatDivider = styled('span')({
  display: 'block',
  height: '100%',
  background: 'rgba(0,0,0,0.1)',
});

const MailBox = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginTop: '20px',
  padding: '15px 17px',
  borderRadius: '6px',
  background: '#f3f5fb',
});

const MailLine = styled('div')({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.8)',
});

const WatchLine = styled('div')({
  fontSize: '0.8125rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.6)',
  marginTop: '4px',
});

const Footer = styled('div')({
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '16px 36px 24px',
  borderTop: '1px solid rgba(0,0,0,0.08)',
});

export interface AssessorRoundModalProps {
  open: boolean;
  sentCount: number;
  roundNumber: number;
  approvedCount: number;
  onClose: () => void;
}

export default function AssessorRoundModal({ open, sentCount, roundNumber, approvedCount, onClose }: AssessorRoundModalProps): JSX.Element {
  const one = sentCount === 1;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='QC round sent for review'
      PaperProps={{
        sx: {
          width: 540,
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '6px',
        },
      }}
      maxWidth={false}
    >
      <Body>
        <IconBadge>
          <SendIcon sx={{ fontSize: 26 }} />
        </IconBadge>
        <Eyebrow>Round {roundNumber}</Eyebrow>
        <Title>All {sentCount} QC Issue{one ? '' : 's'} are with TPN</Title>
        <Lede>
          {one
            ? 'Your response has been submitted to TPN. TPN Admin will review it and respond.'
            : 'Your responses have been submitted to TPN. TPN Admin will review them and respond.'}
        </Lede>
        <StatsGrid>
          <StatCell>
            <StatValue color={ACCENT_BLUE_DARK}>{sentCount}</StatValue>
            <StatLabel>{one ? 'Issue sent for review' : 'Issues sent for review'}</StatLabel>
          </StatCell>
          <StatDivider />
          <StatCell>
            <StatValue color={SUCCESS_GREEN}>{approvedCount}</StatValue>
            <StatLabel>Already approved</StatLabel>
          </StatCell>
        </StatsGrid>
        <MailBox>
          <MailIcon sx={{ fontSize: 19, color: ACCENT_BLUE, flex: 'none', mt: '1px' }} />
          <div style={{ minWidth: 0 }}>
            <MailLine>TPN Admin has been emailed.</MailLine>
            <WatchLine>
              {one
                ? 'If it comes back it will land here as Needs Update; if not, this assessment moves to scoring.'
                : 'Anything that comes back lands here as Needs Update. If nothing does, this assessment moves to scoring.'}
            </WatchLine>
          </div>
        </MailBox>
      </Body>
      <Footer>
        <PrimaryButton type='button' onClick={onClose}>Close</PrimaryButton>
      </Footer>
    </Dialog>
  );
}
